import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { delay, map, tap, catchError, take } from 'rxjs/operators';
import { Product, ProductCategory, Cart } from '../models/kiosk.models';
import { environment } from '../../../environments/environment';
import { LogService } from '../../services/log.service';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private http = inject(HttpClient);
  private log = inject(LogService);

  // Produits mock uniquement comme fallback si l'API échoue
  private mockProducts: Product[] = [
    {
      id: 1,
      name: 'Burger Classique',
      price: 8.5,
      stock: 8,
      icon: '🍔',
      category: ProductCategory.PLATS,
      isActive: true,
      replacementProductId: 2,
      minStockAlert: 3,
    },
    {
      id: 2,
      name: 'Burger Bacon',
      price: 9.5,
      stock: 2,
      icon: '🍔',
      category: ProductCategory.PLATS,
      isActive: true,
      replacementProductId: 1,
      minStockAlert: 3,
    },
    {
      id: 3,
      name: 'Burger Végétarien',
      price: 8.0,
      stock: 6,
      icon: '🥗',
      category: ProductCategory.PLATS,
      isActive: true,
      replacementProductId: 1,
      minStockAlert: 2,
    },
    {
      id: 4,
      name: 'Hot Dog',
      price: 6.0,
      stock: 5,
      icon: '🌭',
      category: ProductCategory.PLATS,
      isActive: true,
      minStockAlert: 2,
    },
    {
      id: 5,
      name: 'Wrap Poulet',
      price: 7.5,
      stock: 4,
      icon: '🌯',
      category: ProductCategory.PLATS,
      isActive: true,
      replacementProductId: 1,
      minStockAlert: 2,
    },
    {
      id: 6,
      name: 'Frites',
      price: 4.0,
      stock: 10,
      icon: '🍟',
      category: ProductCategory.SNACKS,
      isActive: true,
      minStockAlert: 3,
    },
    {
      id: 7,
      name: 'Nuggets (6 pcs)',
      price: 5.5,
      stock: 0,
      icon: '🍗',
      category: ProductCategory.SNACKS,
      isActive: true,
      minStockAlert: 3,
    },
    {
      id: 8,
      name: 'Onion Rings',
      price: 4.5,
      stock: 7,
      icon: '🧅',
      category: ProductCategory.SNACKS,
      isActive: true,
      minStockAlert: 2,
    },
    {
      id: 9,
      name: 'Crêpe Nutella',
      price: 5.0,
      stock: 6,
      icon: '🥞',
      category: ProductCategory.DESSERTS,
      isActive: true,
      replacementProductId: 11,
      minStockAlert: 2,
    },
    {
      id: 10,
      name: 'Crêpe Sucre',
      price: 4.0,
      stock: 5,
      icon: '🥞',
      category: ProductCategory.DESSERTS,
      isActive: true,
      replacementProductId: 9,
      minStockAlert: 2,
    },
    {
      id: 11,
      name: 'Gaufre',
      price: 5.5,
      stock: 4,
      icon: '🧇',
      category: ProductCategory.DESSERTS,
      isActive: true,
      replacementProductId: 9,
      minStockAlert: 2,
    },
    {
      id: 12,
      name: 'Glace Vanille',
      price: 4.5,
      stock: 3,
      icon: '🍦',
      category: ProductCategory.DESSERTS,
      isActive: true,
      minStockAlert: 2,
    },
    {
      id: 13,
      name: 'Coca Cola',
      price: 3.0,
      stock: 9,
      icon: '🥤',
      category: ProductCategory.BOISSONS,
      isActive: true,
      minStockAlert: 4,
    },
    {
      id: 14,
      name: 'Pepsi',
      price: 3.0,
      stock: 8,
      icon: '🥤',
      category: ProductCategory.BOISSONS,
      isActive: true,
      minStockAlert: 4,
    },
    {
      id: 15,
      name: 'Eau Minérale',
      price: 2.0,
      stock: 10,
      icon: '💧',
      category: ProductCategory.BOISSONS,
      isActive: true,
      minStockAlert: 5,
    },
    {
      id: 16,
      name: "Jus d'Orange",
      price: 3.5,
      stock: 7,
      icon: '🧃',
      category: ProductCategory.BOISSONS,
      isActive: true,
      minStockAlert: 3,
    },
    {
      id: 17,
      name: 'Café',
      price: 2.5,
      stock: 9,
      icon: '☕',
      category: ProductCategory.BOISSONS,
      isActive: true,
      minStockAlert: 4,
    },
  ];

  private products: Product[] = [];
  private _products = signal<Product[]>([]);
  readonly productsSignal = this._products.asReadonly();
  /** @deprecated Use productsSignal signal instead */
  readonly products$ = toObservable(this.productsSignal);

  // Stocks de sauvegarde pour restaurer en cas d'annulation
  private initialStocks: Map<number | string, number> = new Map();
  private isOrderInProgress: boolean = false;
  private productsLoaded = false;

  constructor() {
    // Charger les produits depuis le backend au démarrage
    this.loadProductsFromBackend();
  }

  /**
   * Charge les produits depuis le backend ou utilise les mock selon environment.mockApi
   */
  private loadProductsFromBackend(): void {
    if (this.productsLoaded) return;

    // Si mockApi est activé, utiliser les données mock directement
    if (environment.mockApi) {
      this.log.debug('Mode Mock activé - Utilisation des données mockées');
      // Restaurer les stocks persistés en sessionStorage
      this.products = this.loadMockProductsWithPersistedStocks();
      this._products.set(this.products);
      this.productsLoaded = true;
      return;
    }

    // Sinon, charger depuis le backend
    this.log.debug('Chargement des produits depuis le backend');
    this.http
      .get<{ success: boolean; data: any[]; source?: string }>(
        `${environment.apiUrl}/products/kiosk-stocks`, // Nouvel endpoint avec stocks du service actif
      )
      .pipe(
        map((response) => {
          this.log.debug('Source des stocks', {
            source: response.source || 'unknown',
          });
          // Mapper les champs du backend vers le format frontend
          return (response.data || []).map((p: any) => ({
            id: p._id, // IMPORTANT: utiliser _id MongoDB comme ID
            name: p.name,
            price: p.sellPrice || p.price || 0,
            stock: p.quantityAvailable ?? p.quantity ?? p.stock ?? 0, // Priorité: quantityAvailable (service actif) > quantity (setup)
            quantityAvailable: p.quantityAvailable,
            quantity: p.quantity,
            icon: p.productIcon || this.getIconForProduct(p.name), // Utiliser productIcon de la DB d'abord
            category: this.getCategoryForProduct(p.category),
            isActive: true,
            minStockAlert: 3,
          }));
        }),
        catchError((error) => {
          this.log.error('Erreur chargement produits depuis le backend', {
            error: (error as Error).message,
          });
          this.log.warn(
            'Aucun produit disponible - Vérifiez que le backend est démarré',
          );
          return of([]); // Retourner un tableau vide au lieu des mock
        }),
      )
      .subscribe((products) => {
        this.products = products;
        this._products.set(products);
        if (products.length > 0) {
          this.log.info('Produits chargés depuis le backend', {
            count: products.length,
          });
          this.log.debug('Exemple ID', { id: products[0].id });
        } else {
          this.log.warn(
            'Aucun produit chargé - Créez des produits via le backend',
          );
        }
        this.productsLoaded = true;
      });
  }

  private getIconForProduct(name: string): string {
    const icons: Record<string, string> = {
      burger: '🍔',
      wrap: '🌯',
      hot: '🌭',
      frite: '🍟',
      nugget: '🍗',
      onion: '🧅',
      crêpe: '🥞',
      gaufre: '🧇',
      glace: '🍦',
      coca: '🥤',
      pepsi: '🥤',
      eau: '💧',
      jus: '🧃',
      café: '☕',
      salade: '🥗',
    };
    const nameLower = name.toLowerCase();
    for (const [key, icon] of Object.entries(icons)) {
      if (nameLower.includes(key)) return icon;
    }
    return '🍽️';
  }

  private getCategoryForProduct(category?: string): ProductCategory {
    if (!category) return ProductCategory.PLATS;
    const catLower = category.toLowerCase();
    if (catLower.includes('boisson')) return ProductCategory.BOISSONS;
    if (catLower.includes('dessert')) return ProductCategory.DESSERTS;
    if (catLower.includes('snack')) return ProductCategory.SNACKS;
    return ProductCategory.PLATS;
  }

  getProducts(): Observable<Product[]> {
    return this.products$;
  }

  getProductById(id: number | string): Observable<Product | undefined> {
    return this.products$.pipe(
      map((products) => products.find((p) => p.id === id)),
      take(1), // Prendre seulement la première valeur pour éviter les boucles infinies
    );
  }

  /**
   * Sauvegarde les stocks initiaux au début d'une commande
   */
  startOrder(cart: Cart): void {
    if (!this.isOrderInProgress) {
      // Sauvegarder les stocks actuels de tous les produits affectés
      cart.items.forEach((item) => {
        const product = this.products.find((p) => p.id === item.product.id);
        if (product && !this.initialStocks.has(product.id)) {
          this.initialStocks.set(product.id, product.stock);
        }
      });
      this.isOrderInProgress = true;
      this.log.debug('Order started - Initial stocks saved');
    }
  }

  /**
   * Décrémente le stock d'un produit (lors de l'ajout au panier)
   */
  decrementStock(productId: number | string, quantity: number): void {
    this.log.debug('decrementStock called', {
      productId,
      quantity,
    });
    const product = this.products.find((p) => p.id === productId);
    if (product && product.stock >= quantity) {
      product.stock -= quantity;
      this.log.debug('Stock decremented', {
        product: product.name,
        from: product.stock + quantity,
        to: product.stock,
      });
      this._products.set([...this.products]);

      // Persister en sessionStorage si mode mock
      if (environment.mockApi) {
        this.persistMockStocks();
      }
    }
  }

  /**
   * Incrémente le stock d'un produit (lors du retrait du panier)
   */
  incrementStock(productId: number | string, quantity: number): void {
    const product = this.products.find((p) => p.id === productId);
    if (product) {
      product.stock += quantity;
      this.log.debug('Stock incremented', {
        product: product.name,
        from: product.stock - quantity,
        to: product.stock,
      });
      this._products.set([...this.products]);

      // Persister en sessionStorage si mode mock
      if (environment.mockApi) {
        this.persistMockStocks();
      }
    }
  }

  /**
   * Met à jour le stock en base de données
   */
  private updateStockInBackend(
    productId: number | string,
    newStock: number,
  ): void {
    this.http
      .patch(`${environment.apiUrl}/products/${productId}/stock`, {
        stock: newStock,
      })
      .pipe(
        catchError((error) => {
          this.log.warn(
            'Erreur mise à jour stock en DB, changement local conservé',
          );
          return of(null);
        }),
      )
      .subscribe();
  }

  /**
   * Restaure les stocks à leur état initial (annulation de commande)
   */
  restoreStocks(): void {
    if (this.isOrderInProgress) {
      this.initialStocks.forEach((initialStock, productId) => {
        const product = this.products.find((p) => p.id === productId);
        if (product) {
          this.log.debug('Restoring stock', {
            product: product.name,
            from: product.stock,
            to: initialStock,
          });
          product.stock = initialStock;
        }
      });
      this._products.set([...this.products]);
      this.initialStocks.clear();
      this.isOrderInProgress = false;
      this.log.debug('Order cancelled - Stocks restored');
    }
  }

  /**
   * Confirme la commande et réinitialise le système de sauvegarde
   */
  confirmOrder(): void {
    if (this.isOrderInProgress) {
      this.initialStocks.clear();
      this.isOrderInProgress = false;
      this.log.debug('Order confirmed - Stocks finalized');
    }
  }

  checkStockAvailability(
    productId: number | string,
    quantity: number,
    currentCart?: Cart,
  ): Observable<{
    available: boolean;
    currentStock: number;
    message?: string;
    replacementProduct?: Product;
    alternativeProducts?: Product[];
  }> {
    const product = this.products.find((p) => p.id === productId);

    if (!product) {
      return throwError(() => new Error('Produit introuvable'));
    }

    const available = product.stock >= quantity;
    let replacementProduct: Product | undefined;
    let alternativeProducts: Product[] = [];

    this.log.debug('checkStockAvailability', {
      product: product.name,
      stock: product.stock,
      quantity,
    });

    if (!available) {
      // Vérifier le produit de remplacement configuré
      if (product.replacementProductId) {
        const configuredReplacement = this.products.find(
          (p) => p.id === product.replacementProductId,
        );
        this.log.debug('Configured replacement', {
          name: configuredReplacement?.name,
          stock: configuredReplacement?.stock,
        });
        // Utiliser seulement si en stock
        if (configuredReplacement && configuredReplacement.stock > 0) {
          replacementProduct = configuredReplacement;
        }
      }

      // Toujours trouver les produits alternatifs disponibles de la même catégorie
      alternativeProducts = this.products.filter((p) => {
        if (
          p.category !== product.category ||
          p.id === product.id ||
          p.id === replacementProduct?.id ||
          !p.isActive
        ) {
          return false;
        }
        this.log.debug('Alternative', { name: p.name, stock: p.stock });
        return p.stock > 0;
      });
      this.log.debug('Alternative products found', {
        count: alternativeProducts.length,
        names: alternativeProducts.map((p) => p.name),
      });
    }

    const result = {
      available,
      currentStock: product.stock,
      message: available
        ? 'Stock disponible'
        : replacementProduct
          ? `Stock insuffisant. Produit de remplacement disponible: ${replacementProduct.name}`
          : 'Produit en rupture de stock',
      replacementProduct,
      alternativeProducts,
    };
    this.log.debug('Stock check result', {
      available: result.available,
      currentStock: result.currentStock,
    });

    return of(result);
  }

  updateStock(productId: number, quantityChange: number): Observable<Product> {
    const product = this.products.find((p) => p.id === productId);

    if (!product) {
      return throwError(() => new Error('Produit introuvable'));
    }

    product.stock = Math.max(0, product.stock + quantityChange);
    this._products.set([...this.products]);

    // Persister en sessionStorage si mode mock
    if (environment.mockApi) {
      this.persistMockStocks();
    }

    return of(product);
  }

  getLowStockProducts(): Observable<Product[]> {
    return of(
      this.products.filter((p) => p.stock <= p.minStockAlert && p.isActive),
    );
  }

  // ==================== MOCK PERSISTENCE ====================

  private readonly MOCK_STOCKS_KEY = 'kiosk_mock_stocks';

  /**
   * Charge les produits mock avec les stocks persistés en sessionStorage
   */
  private loadMockProductsWithPersistedStocks(): Product[] {
    const savedStocks = this.getSavedMockStocks();

    if (Object.keys(savedStocks).length > 0) {
      this.log.debug('Restauration des stocks persistés depuis sessionStorage');
    }

    return this.mockProducts.map((product) => ({
      ...product,
      stock: savedStocks[product.id] ?? product.stock,
    }));
  }

  /**
   * Persiste les stocks actuels en sessionStorage (mode mock uniquement)
   */
  private persistMockStocks(): void {
    const stocks: Record<number | string, number> = {};
    this.products.forEach((p) => {
      stocks[p.id] = p.stock;
    });
    sessionStorage.setItem(this.MOCK_STOCKS_KEY, JSON.stringify(stocks));
    this.log.debug('Stocks persistés en sessionStorage');
  }

  /**
   * Récupère les stocks sauvegardés depuis sessionStorage
   */
  private getSavedMockStocks(): Record<number | string, number> {
    try {
      const saved = sessionStorage.getItem(this.MOCK_STOCKS_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }

  /**
   * Réinitialise les stocks mock aux valeurs par défaut
   */
  resetMockStocks(): void {
    sessionStorage.removeItem(this.MOCK_STOCKS_KEY);
    this.products = [...this.mockProducts];
    this._products.set(this.products);
    this.log.debug('Stocks mock réinitialisés');
  }
}
